import React from 'react';
import { makeStyles, Box, useTheme, Typography } from '@material-ui/core';
import { getCustomColors } from '../../styles/CustomColors';
import moment from 'moment';
import clsx from 'clsx';
import { COLOR_GREY } from '../utilities/MaterialWatchman';
import StartBloodBadge from '../../resources/images/start-blood-badge.svg';
import StartImagingBadge from '../../resources/images/start-imaging-badge.svg';
import StartBloodAndImagingBadge from '../../resources/images/start-blood-and-imaging-badge.svg';
import BloodBadge from '../../resources/images/blood-badge.svg';
import ImagingBadge from '../../resources/images/imaging-big-badge.svg';
import BloodAndImagingBadge from '../../resources/images/blood-and-imaging-badge.svg';
import EndBloodBadge from '../../resources/images/end-blood-badge.svg';
import EndImagingBadge from '../../resources/images/end-imaging-badge.svg';
import EndBloodAndImagingBadge from '../../resources/images/end-blood-and-imaging-badge.svg';
import EndOftreatmentBadge from '../../resources/images/circle-check-big.svg';
import DateOfDiagnosisBadge from '../../resources/images/rocket-badge-big.svg';
import { isoMoment, today, visitYearRelativeToDiagnosisDate, YearMonth } from '../utilities/commonUtilities/dateTools';
import { isTaskPresent } from '../../utils/VisitTaskHandler';
import * as TaskNames from '../home/TaskNames';
import Popover from '@material-ui/core/Popover';
import { ReactComponent as Cancel } from '../../resources/images/close-small.svg';
import { yearsNumList } from '../utilities/commonUtilities/dateTools';

const Colors = getCustomColors();
export const CALENDAR_TABLE_WIDTH = '960px';
const VISIT_CELL_HEIGHT = 90;
const VISIT_POPOVER_HEIGHT = 344.3;
const VISIT_POPOVER_WIDTH = 479;

const useStyles = makeStyles(theme => ({
    monthCell: {
        textAlign: 'center',
        background: Colors.COLOR_WHITE,
        padding: `${theme.spacing(1.125)}px ${theme.spacing(0.6875)}px`,
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '125%',
        width: '100%',
    },
    monthRow: {
        display: 'flex',
        width: '100%',
        height: theme.spacing(4),
        marginTop: theme.spacing(2.5),
        minWidth: CALENDAR_TABLE_WIDTH,
    },
    yearRow: {
        display: 'flex',
        width: '100%',
        marginTop: theme.spacing(0.5),
        minWidth: CALENDAR_TABLE_WIDTH,
    },
    currentDate: {
        fontWeight: 700,
    },
    visitCell: {
        textAlign: 'center',
        background: Colors.COLOR_WHITE,
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '9.99599px',
        lineHeight: '125%',
        width: `100%`,
        height: `${VISIT_CELL_HEIGHT}px`,
        display: 'flex',
    },
    visitIconCell: {},
    currentCell: {
        outline: `3px solid ${theme.palette.primary.main}`,
        zIndex: 1,
    },
    doneCell: {
        background: COLOR_GREY.light,
    },

    doneIcon: {
        filter: 'grayscale(1)',
        '& circle': {
            fill: 'rgba(255,255,255,0.4)',
        },
    },
    visitPopoverContent: {
        width: `${VISIT_POPOVER_WIDTH}px`,
        height: `${VISIT_POPOVER_HEIGHT}px`,
        'overflow-y': 'scroll',
        '-ms-overflow-style': 'none' /* Internet Explorer 10+ */,
        'scrollbar-width': 'none' /* Firefox */,
        '&::-webkit-scrollbar': {
            /* WebKit */ width: 0,
            height: 0,
        },
    },
    visitPopoverHeader: {
        boxShadow: '0px 0.687231px 2.74892px rgba(186, 186, 186, 0.25)',
        paddingTop: theme.spacing(2),
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingBottom: theme.spacing(1.75),
        position: 'fixed',
        width: `${VISIT_POPOVER_WIDTH}px`,
        zIndex: '9999',
        background: Colors.COLOR_WHITE,
        borderRadius: '17.1808px 17.1808px 0px 0px',
    },
    visitPopoverSubHeader: {
        color: theme.palette.primary.main,
    },
    pointerCursor: {
        cursor: 'pointer',
    },
    visitCellClicked: {
        background: theme.palette.primary.light,
        boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        zIndex: 99,
    },
    visitCellHover: {
        cursor: 'pointer',
        '&:hover': {
            background: theme.palette.primary.light,
        },
    },
    popoverPaper: {
        boxShadow: '0px 6.18508px 13.0574px rgba(0, 0, 0, 0.15)',
        borderRadius: '17.1808px',
    },
    infoHeader: {
        marginTop: theme.spacing(3),
    },
    visitPopoverContentBody: {
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3),
        paddingBottom: theme.spacing(2.5),
        paddingTop: theme.spacing(8.75),
    },
    infoBadge: {
        marginTop: theme.spacing(2.5),
    },
    infoSubHeader: {
        fontSize: '12px',
        marginTop: '17px',
        marginBottom: '6px',
    },
    infoParagraph: {
        fontSize: '12px',
    },
    imagingInfoFirstParagraph: {
        marginTop: theme.spacing(2.125),
    },
}));

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const currentYear = today.year();
const currentMonthIndx = today.month();

const VisitObj = () => {
    return {
        month: '',
        icon: null,
        isDoneVisit: false,
        isPeripheralIcon: false,
        iconName: '',
        visitYearRelativeToDiagnosisDate: null,
    };
};

export const ICON_NAMES = {
    blood: 'blood',
    imaging: 'imaging',
    bloodAndImaging: 'blood and imaging',
    dateOfDiagnosis: 'date of diagnosis',
    dateOfDiagnosisAndBlood: 'date of diagnosis and blood',
    dateOfDiagnosisAndImaging: 'date of diagnosis and imaging',
    dateOfDiagnosisAndBloodAndImaging: 'date of diagnosis and blood and imaging',
    endOfTreatment: 'end of treatment',
    endOfTreatmentAndBlood: 'end of treatment and blood',
    endOfTreatmentAndImaging: 'end of treatment and imaging',
    endOfTreatmentAndBloodAndImaging: 'end of treatment and blood and imaging',
};

const VisitPopoverHeader = ({ yearCount, year, month, onCancelSelected }) => {
    const theme = useTheme();
    const classes = useStyles();
    return (
        <Box display="flex" justifyContent="space-between" alignItems="center" className={classes.visitPopoverHeader}>
            <Box></Box>
            <Box display="flex" flexDirection="column" alignItems="center">
                <Typography variant="h4">YEAR[{yearCount}]</Typography>
                <Typography variant="caption" className={classes.visitPopoverSubHeader}>
                    [{`${month} ${year}`}]
                </Typography>
            </Box>
            <Box>
                <Cancel aria-labelledby="cancel" fill={theme.palette.primary.dark} onClick={onCancelSelected} className={classes.pointerCursor} />
            </Box>
        </Box>
    );
};

const VisitPopoverContent = ({ onCancelSelected, yearCount, year, month, iconName }) => {
    const classes = useStyles();
    return (
        <Box className={classes.visitPopoverContent}>
            <VisitPopoverHeader onCancelSelected={onCancelSelected} yearCount={yearCount} year={year} month={month} />
            <Box className={classes.visitPopoverContentBody}>
                {[
                    ICON_NAMES.blood,
                    ICON_NAMES.bloodAndImaging,
                    ICON_NAMES.dateOfDiagnosisAndBlood,
                    ICON_NAMES.dateOfDiagnosisAndBloodAndImaging,
                    ICON_NAMES.endOfTreatmentAndBlood,
                    ICON_NAMES.endOfTreatmentAndBloodAndImaging,
                ].includes(iconName) ? (
                    <BloodTestInfo />
                ) : null}
                {[
                    ICON_NAMES.imaging,
                    ICON_NAMES.bloodAndImaging,
                    ICON_NAMES.dateOfDiagnosisAndImaging,
                    ICON_NAMES.dateOfDiagnosisAndBloodAndImaging,
                    ICON_NAMES.endOfTreatmentAndImaging,
                    ICON_NAMES.endOfTreatmentAndBloodAndImaging,
                ].includes(iconName) ? (
                    <ImagingTestInfo />
                ) : null}
            </Box>
        </Box>
    );
};

const CalendarDesktop = ({ visits, dateOfDiagnosis, endOfTreatementDate }) => {
    const years = yearsNumList(dateOfDiagnosis.year() || moment().year(), endOfTreatementDate.year() || moment().add(10, 'years').year());
    return (
        <>
            <MonthRow />
            <CalendarDesktopBody years={years} visits={visits} dateOfDiagnosis={dateOfDiagnosis} endOfTreatementDate={endOfTreatementDate} />
        </>
    );
};

const CalendarDesktopBody = ({ years, visits, dateOfDiagnosis, endOfTreatementDate }) => {
    const visitTaskMap = generateVisitTaskMap(visits);
    const boundaryYearMonth = getBoundaryYearAndMonth(visits);

    return (
        <>
            {years.map((year, indx) => (
                <YearRow
                    year={year}
                    key={indx}
                    dateOfDiagnosis={dateOfDiagnosis}
                    endOfTreatementDate={endOfTreatementDate}
                    visitTaskMap={visitTaskMap}
                    boundaryYearMonth={boundaryYearMonth}
                    yearCount={indx}
                />
            ))}
        </>
    );
};

function MonthCell({ month }) {
    const classes = useStyles();
    return (
        <div
            className={clsx(classes.monthCell, {
                [classes.currentDate]: months[currentMonthIndx] === month,
            })}
        >
            {month ?? ''}
        </div>
    );
}

function MonthRow() {
    const classes = useStyles();
    return (
        <div className={classes.monthRow}>
            <MonthCell />
            {months.map((month, indx) => (
                <MonthCell month={month} key={indx} />
            ))}
        </div>
    );
}

function VisitCell({ year, Icon, isCurrentVisit, isDoneVisit, month, yearCount, isYearCell, isPeripheralIcon, iconName }) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [anchorElClicked, setAnchorElClicked] = React.useState(false);
    const hasPopover = Icon && !isDoneVisit && !isPeripheralIcon;

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
        setAnchorElClicked(true);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setAnchorElClicked(false);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <div
                className={clsx(classes.visitCell, {
                    [classes.currentDate]: currentYear === year && isYearCell,
                    [classes.currentCell]: isCurrentVisit,
                    [classes.doneCell]: isDoneVisit,
                    [classes.visitIconCell]: Icon,
                    [classes.visitCellHover]: hasPopover,
                    [classes.visitCellClicked]: anchorElClicked && hasPopover,
                })}
                onClick={handleClick}
            >
                {isYearCell ? year : ''}
                {Icon && (
                    <img
                        src={Icon}
                        alt="calendar icon"
                        aria-labelledby="calendar icon"
                        className={clsx({
                            [classes.doneIcon]: isDoneVisit,
                        })}
                    />
                )}
            </div>
            {hasPopover && (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    classes={{
                        paper: classes.popoverPaper,
                    }}
                >
                    <VisitPopoverContent onCancelSelected={handleClose} yearCount={yearCount} year={year} month={month} iconName={iconName} />
                </Popover>
            )}
        </>
    );
}

function YearRow({ year, dateOfDiagnosis, endOfTreatementDate, visitTaskMap, boundaryYearMonth, yearCount }) {
    const classes = useStyles();
    const row = months.map((month, monthIndx) => {
        const visitObj = VisitObj();
        visitObj.month = month;
        visitObj.isDoneVisit = isVisitDone(year, monthIndx, boundaryYearMonth, dateOfDiagnosis, endOfTreatementDate);
        const { icon, isPeripheralIcon, iconName, visitDate } = getIcon(year, monthIndx, visitTaskMap, dateOfDiagnosis, endOfTreatementDate);
        visitObj.icon = icon;
        visitObj.isPeripheralIcon = isPeripheralIcon;
        visitObj.iconName = iconName;
        visitObj.visitYearRelativeToDiagnosisDate = visitYearRelativeToDiagnosisDate(visitDate, dateOfDiagnosis);
        return visitObj;
    });

    return (
        <div className={classes.yearRow}>
            <VisitCell year={year} isYearCell={true} />
            {row.map((visitObj, indx) => (
                <VisitCell
                    key={indx}
                    Icon={visitObj.icon}
                    isCurrentVisit={year === currentYear && months[currentMonthIndx] === visitObj.month}
                    isDoneVisit={visitObj.isDoneVisit}
                    month={visitObj.month}
                    yearCount={visitObj.visitYearRelativeToDiagnosisDate}
                    year={year}
                    isPeripheralIcon={visitObj.isPeripheralIcon}
                    iconName={visitObj.iconName}
                />
            ))}
        </div>
    );
}

function getIcon(year, monthIndx, visitTaskMap, dateOfDiagnosis, endOfTreatementDate) {
    const tasks = visitTaskMap[`${year}+${months[monthIndx]}`]?.tasks ?? [];
    const date = visitTaskMap[`${year}+${months[monthIndx]}`]?.date ?? null;
    const bloodWorkPresent = isTaskPresent({ tasks }, TaskNames.LAB_WORK);
    const imagingPresent = isTaskPresent({ tasks }, TaskNames.IMAGING);

    /* Retrieve icon corresponding to year and month */
    if (dateOfDiagnosis.year() === year && dateOfDiagnosis.month() === monthIndx) {
        if (bloodWorkPresent && imagingPresent) {
            return { icon: StartBloodAndImagingBadge, isPeripheralIcon: false, iconName: ICON_NAMES.dateOfDiagnosisAndBloodAndImaging, visitDate: date };
        } else if (bloodWorkPresent) {
            return { icon: StartBloodBadge, isPeripheralIcon: false, iconName: ICON_NAMES.dateOfDiagnosisAndBlood, visitDate: date };
        } else if (imagingPresent) {
            return { icon: StartImagingBadge, isPeripheralIcon: false, iconName: ICON_NAMES.dateOfDiagnosisAndImaging, visitDate: date };
        } else {
            return { icon: DateOfDiagnosisBadge, isPeripheralIcon: true, iconName: ICON_NAMES.dateOfDiagnosis, visitDate: date };
        }
    }

    if (endOfTreatementDate.year() === year && endOfTreatementDate.month() === monthIndx) {
        if (bloodWorkPresent && imagingPresent) {
            return { icon: EndBloodAndImagingBadge, isPeripheralIcon: false, iconName: ICON_NAMES.endOfTreatmentAndBloodAndImaging, visitDate: date };
        } else if (bloodWorkPresent) {
            return { icon: EndBloodBadge, isPeripheralIcon: false, iconName: ICON_NAMES.endOfTreatmentAndBlood, visitDate: date };
        } else if (imagingPresent) {
            return { icon: EndImagingBadge, isPeripheralIcon: false, iconName: ICON_NAMES.endOfTreatmentAndImaging, visitDate: date };
        } else {
            return { icon: EndOftreatmentBadge, isPeripheralIcon: true, iconName: ICON_NAMES.endOfTreatment, visitDate: date };
        }
    }

    if (bloodWorkPresent && imagingPresent) {
        return { icon: BloodAndImagingBadge, isPeripheralIcon: false, iconName: ICON_NAMES.bloodAndImaging, visitDate: date };
    } else if (bloodWorkPresent) {
        return { icon: BloodBadge, isPeripheralIcon: false, iconName: ICON_NAMES.blood, visitDate: date };
    } else if (imagingPresent) {
        return { icon: ImagingBadge, isPeripheralIcon: false, iconName: ICON_NAMES.imaging, visitDate: date };
    } else {
        return { icon: null, isPeripheralIcon: false, iconName: '', visitDate: date };
    }
}

function generateVisitTaskMap(visits) {
    const visitTaskMap = {};
    visits.forEach(visit => {
        const date = isoMoment(visit.taskOpenDate);
        const visitYear = date.year();
        const visitMonth = months[date.month()];
        let existingTasks = visitTaskMap[`${visitYear}+${visitMonth}`] ?? { tasks: [], date: null };
        visitTaskMap[`${visitYear}+${visitMonth}`] = {
            tasks: [...existingTasks?.tasks, ...(visit.tasks ? visit.tasks : [])],
            date, // take the latest task open date
        };
    });
    return visitTaskMap;
}

function getBoundaryYearAndMonth(visits) {
    let boundaryYear = currentYear;
    let boundaryMonthIndx = currentMonthIndx;

    for (const visit of visits) {
        if (visit.visitState !== 'CLOSED') {
            boundaryYear = isoMoment(visit.taskOpenDate).year();
            boundaryMonthIndx = isoMoment(visit.taskOpenDate).month();
            break;
        }
    }

    let boundaryYearMonth = new YearMonth(boundaryYear, boundaryMonthIndx);
    let currentYearMonth = new YearMonth(currentYear, currentMonthIndx);

    if (boundaryYearMonth.isBefore(currentYearMonth)) {
        return boundaryYearMonth;
    } else {
        // check if current year and monthIndx correspond to closed visit
        let isCurrentYearMonthClosed = false;
        for (const visit of visits) {
            const visitYear = isoMoment(visit.taskOpenDate).year();
            const visitMonthIndx = isoMoment(visit.taskOpenDate).month();
            const visitYearMonth = new YearMonth(visitYear, visitMonthIndx);
            if (visit.visitState === 'CLOSED' && visitYearMonth.isSame(currentYearMonth)) {
                isCurrentYearMonthClosed = true;
                break;
            }
        }

        if (isCurrentYearMonthClosed) {
            // use the next cell for boundaryYearMonth
            if (currentMonthIndx === 11) {
                // we are at the last month of current year
                boundaryMonthIndx = 0;
                boundaryYear = currentYear + 1;
            } else {
                boundaryMonthIndx = currentMonthIndx + 1;
                boundaryYear = currentYear;
            }
            return new YearMonth(boundaryYear, boundaryMonthIndx);
        } else {
            return currentYearMonth;
        }
    }
}

function isVisitDone(year, monthIndx, boundaryYearMonth, dateOfDiagnosis, endOfTreatementDate) {
    const visitYearMonth = new YearMonth(year, monthIndx);
    const dateOfDiagnosisYearMonth = new YearMonth(dateOfDiagnosis.year(), dateOfDiagnosis.month());
    const endOfTreatementDateYearMonth = new YearMonth(endOfTreatementDate.year(), endOfTreatementDate.month());

    return (
        visitYearMonth.isBefore(boundaryYearMonth) &&
        (visitYearMonth.isAfter(dateOfDiagnosisYearMonth) || visitYearMonth.isSame(dateOfDiagnosisYearMonth)) &&
        (visitYearMonth.isBefore(endOfTreatementDateYearMonth) || visitYearMonth.isSame(endOfTreatementDateYearMonth))
    );
}

export function BloodTestInfo({ customClasses }) {
    const classes = customClasses ?? useStyles();
    return (
        <>
            <img src={BloodBadge} alt="Blood badge" aria-labelledby="Blood badge" className={classes.infoBadge} />
            <Typography variant="h3" className={classes.infoHeader}>
                Blood Test
            </Typography>
            <Typography variant="h4" className={classes.infoSubHeader}>
                HCG
            </Typography>
            <Typography variant="body1" className={classes.infoParagraph}>
                This test measures the level of a protein called Human Chorionic Gonadotropin (HCG) in your blood. Many testicular cancers make high levels of
                tumor marker proteins like HCG. If your HCG appears elevated this may suggest your tumour has relapsed.
            </Typography>
            <Typography variant="h4" className={classes.infoSubHeader}>
                AFP
            </Typography>
            <Typography variant="body1" className={classes.infoParagraph}>
                This test measures the level of a protein called Alpha-fetoprotein (AFP) in your blood. High levels of AFP may indicate that your testicular
                tumor has relapsed.
            </Typography>
            <Typography variant="h4" className={classes.infoSubHeader}>
                Serum LH
            </Typography>
            <Typography variant="body1" className={classes.infoParagraph}>
                This test measures the level of a protein called Luteinizing Hormone (LH) in your blood. LH is the brain's signal to the testicle to make
                testosterone. We monitor this to help us understand how your remaining testicle is functioning. Abnormalities in LH do NOT indicate a relapse of
                cancer.
            </Typography>
            <Typography variant="h4" className={classes.infoSubHeader}>
                FSH
            </Typography>
            <Typography variant="body1" className={classes.infoParagraph}>
                This test measures the level of a protein called Follicle-stimulating Hormone (FSH) in your blood. FSH is part of the brain's signal to the
                testicle to make sperm. We monitor this to help us understand how your remaining testicle is functioning. Abnormalities in FSH do NOT indicate a
                relapse of cancer.
            </Typography>
            <Typography variant="h4" className={classes.infoSubHeader}>
                Total Testosterone
            </Typography>
            <Typography variant="body1" className={classes.infoParagraph}>
                This test measures the amount of total Testosterone in your blood. Testosterone is a male sex hormone (androgen). We monitor this to help us
                understand how your remaining testicle is functioning.
            </Typography>
        </>
    );
}

export function ImagingTestInfo({ customClasses }) {
    const classes = customClasses ?? useStyles();
    return (
        <>
            <img src={ImagingBadge} alt="imaging badge" aria-labelledby="imaging badge" className={classes.infoBadge} />
            <Typography variant="h3" className={classes.infoHeader}>
                Imaging Test
            </Typography>
            <Typography variant="body1" className={clsx(classes.infoParagraph, classes.imagingInfoFirstParagraph)}>
                A computerized tomography (CT) scan combines a series of X-ray images taken from different angles around your body and uses computer processing
                to create cross-sectional images (slices).
            </Typography>
            <Typography variant="h4" className={classes.infoSubHeader}>
                CT Abdomen and Pelvis
            </Typography>
            <Typography variant="body1" className={classes.infoParagraph}>
                Computed tomography (CT) of the abdomen and pelvis is a diagnostic imaging test used to assess if there are signs of cancer relapse in the
                abdomen. We are particularly interested in any enlargement of the lymph nodes in the abdomen. CT scanning is fast, painless, noninvasive and
                accurate. Most commonly the scan will involve drinking a fluid called "contrast" to help the CT distinguish bowel from lymph nodes. Usually an
                intravenous contrast is not required.
            </Typography>
            <Typography variant="h4" className={classes.infoSubHeader}>
                CT Thorax
            </Typography>
            <Typography variant="body1" className={classes.infoParagraph}>
                A chest CT (Computed Tomography) scan is an imaging method that uses x-rays to create cross-sectional pictures of the chest and upper abdomen.
                We are monitoring the chest for signs of cancer relapse.
            </Typography>
            <Typography variant="h4" className={classes.infoSubHeader}>
                Chest X-Ray
            </Typography>
            <Typography variant="body1" className={classes.infoParagraph}>
                A Chest X-ray alone is used for Stage 1 Seminoma patients towards the end of their surveillance. A Chest X-ray can see if the cancer has spread
                to your lungs or lymph nodes in the middle of your chest.
            </Typography>
        </>
    );
}

export default CalendarDesktop;
